import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';

const UserProfile = () => {
    const { user, updateProfile, requestAdminRole, refreshUserData } = useUser();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        dateOfBirth: ''
    });
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('profile');

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                dateOfBirth: user.dateOfBirth || ''
            });
        }
    }, [user]);

    // ✅ NEW: Manual refresh function
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshUserData();
            setMessage({
                type: 'success',
                text: 'Profile data refreshed successfully!'
            });
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Failed to refresh data: ' + error.message
            });
        } finally {
            setRefreshing(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await updateProfile(formData);

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: 'Profile updated successfully!'
                });
            } else {
                setMessage({
                    type: 'error',
                    text: result.message
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Failed to update profile. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdminRequest = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await requestAdminRole();

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: 'Admin request submitted successfully! Waiting for approval.'
                });
                // Auto-refresh after 3 seconds
                setTimeout(async () => {
                    await refreshUserData();
                }, 3000);
            } else {
                setMessage({
                    type: 'error',
                    text: result.message
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Failed to submit admin request. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setMessage({
            type: 'info',
            text: 'Password change feature coming soon!'
        });
    };

    if (!user) {
        return (
            <div className="container-fluid py-5">
                <div className="container">
                    <div className="alert alert-warning text-center">
                        <h4>Access Denied</h4>
                        <p>Please login to view your profile.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 bg-gradient min-vh-100">
            <div className="container">
                {/* Header Section */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 mb-4 profile-header-card">
                            <div className="card-body p-4 p-md-5">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-4">
                                                <i className="fas fa-user-cog text-primary fs-2"></i>
                                            </div>
                                            <div>
                                                <h1 className="h2 text-dark mb-1 fw-bold">Account Settings</h1>
                                                <p className="text-muted mb-0">
                                                    Manage your account settings and preferences
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-md-end">
                                        <div className="d-flex align-items-center justify-content-md-end">
                                            <div className="me-3 text-end d-none d-sm-block">
                                                <div className="fw-bold text-dark">{user.name}</div>
                                                <small className="text-muted">{user.email}</small>
                                                <div className="mt-1">
                                                    <span className={`badge ${user.role === 'admin' ? 'bg-success' :
                                                        user.role === 'pending' ? 'bg-warning' : 'bg-info'
                                                        } bg-opacity-10 text-${user.role === 'admin' ? 'success' :
                                                            user.role === 'pending' ? 'warning' : 'info'
                                                        } border border-${user.role === 'admin' ? 'success' :
                                                            user.role === 'pending' ? 'warning' : 'info'
                                                        } border-opacity-25`}>
                                                        <i className="fas fa-circle me-1" style={{ fontSize: '6px' }}></i>
                                                        {user.role === 'admin' ? 'Admin' :
                                                            user.role === 'pending' ? 'Pending Admin' : 'Regular User'}
                                                    </span>
                                                    {/* Refresh Button */}
                                                    <button
                                                        onClick={handleRefresh}
                                                        disabled={refreshing}
                                                        className="btn btn-outline-primary btn-sm ms-2"
                                                        title="Refresh Profile"
                                                    >
                                                        {refreshing ? (
                                                            <span className="spinner-border spinner-border-sm"></span>
                                                        ) : (
                                                            <i className="fas fa-sync-alt"></i>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="position-relative">
                                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center user-avatar"
                                                    style={{ width: '70px', height: '70px', fontSize: '24px', fontWeight: 'bold' }}>
                                                    {user.name.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="position-absolute bottom-0 end-0 bg-success border border-3 border-white rounded-circle"
                                                    style={{ width: '15px', height: '15px' }}></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Sidebar */}
                    <div className="col-lg-3 mb-4">
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                <h6 className="card-title mb-0 fw-bold text-dark">Account Settings</h6>
                            </div>
                            <div className="list-group list-group-flush border-0">
                                <button
                                    className={`list-group-item list-group-item-action border-0 py-3 px-4 d-flex align-items-center ${activeTab === 'profile' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('profile')}
                                >
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${activeTab === 'profile' ? 'bg-white text-primary' : 'bg-light text-muted'}`}
                                        style={{ width: '36px', height: '36px' }}>
                                        <i className="fas fa-user"></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">Profile Information</div>
                                        <small className={`${activeTab === 'profile' ? 'text-white' : 'text-muted'}`}>Personal details</small>
                                    </div>
                                </button>
                                <button
                                    className={`list-group-item list-group-item-action border-0 py-3 px-4 d-flex align-items-center ${activeTab === 'security' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('security')}
                                >
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${activeTab === 'security' ? 'bg-white text-primary' : 'bg-light text-muted'}`}
                                        style={{ width: '36px', height: '36px' }}>
                                        <i className="fas fa-lock"></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">Security</div>
                                        <small className={`${activeTab === 'security' ? 'text-white' : 'text-muted'}`}>Password & privacy</small>
                                    </div>
                                </button>
                                <button
                                    className={`list-group-item list-group-item-action border-0 py-3 px-4 d-flex align-items-center ${activeTab === 'admin' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('admin')}
                                >
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${activeTab === 'admin' ? 'bg-white text-primary' : 'bg-light text-muted'}`}
                                        style={{ width: '36px', height: '36px' }}>
                                        <i className="fas fa-user-shield"></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">Admin Access</div>
                                        <small className={`${activeTab === 'admin' ? 'text-white' : 'text-muted'}`}>Request admin privileges</small>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Account Status */}
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                <h6 className="card-title mb-0 fw-bold text-dark">Account Status</h6>
                            </div>
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '40px', height: '40px' }}>
                                        <i className="fas fa-check text-success"></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">Status</div>
                                        <span className="badge bg-success">Active</span>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center mb-3">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '40px', height: '40px' }}>
                                        <i className="fas fa-calendar text-primary"></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">Member Since</div>
                                        <div className="text-muted small">
                                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            }) : 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center">
                                    <div className={`bg-${user.role === 'admin' ? 'success' :
                                        user.role === 'pending' ? 'warning' : 'info'
                                        } bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3`}
                                        style={{ width: '40px', height: '40px' }}>
                                        <i className={`fas fa-user-tag text-${user.role === 'admin' ? 'success' :
                                            user.role === 'pending' ? 'warning' : 'info'
                                            }`}></i>
                                    </div>
                                    <div>
                                        <div className="fw-medium">User Role</div>
                                        <div className={`text-capitalize fw-bold text-${user.role === 'admin' ? 'success' :
                                            user.role === 'pending' ? 'warning' : 'info'
                                            }`}>
                                            {user.role === 'admin' ? 'Administrator' :
                                                user.role === 'pending' ? 'Pending Admin Approval' : 'Regular User'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-lg-9">
                        {/* Message Alert */}
                        {message.text && (
                            <div className={`alert alert-dismissible fade show d-flex align-items-center mb-4 ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
                                <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : message.type === 'error' ? 'fa-exclamation-circle' : 'fa-info-circle'} me-2`}></i>
                                <div className="flex-grow-1">{message.text}</div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setMessage({ type: '', text: '' })}
                                ></button>
                            </div>
                        )}

                        {/* Profile Information Tab */}
                        {activeTab === 'profile' && (
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '44px', height: '44px' }}>
                                            <i className="fas fa-user-edit text-primary"></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0 fw-bold text-dark">Profile Information</h5>
                                            <p className="text-muted mb-0 small">Update your personal information</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="name" className="form-label fw-medium">
                                                    Full Name <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-user text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control border-start-0"
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="email" className="form-label fw-medium">
                                                    Email Address
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-envelope text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="email"
                                                        className="form-control border-start-0"
                                                        id="email"
                                                        value={formData.email}
                                                        disabled
                                                    />
                                                </div>
                                                <small className="text-muted">
                                                    Email cannot be changed. Contact support if needed.
                                                </small>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="phone" className="form-label fw-medium">
                                                    Phone Number
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-phone text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="tel"
                                                        className="form-control border-start-0"
                                                        id="phone"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        placeholder="+8801XXXXXXXXX"
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="dateOfBirth" className="form-label fw-medium">
                                                    Date of Birth
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-calendar text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="date"
                                                        className="form-control border-start-0"
                                                        id="dateOfBirth"
                                                        name="dateOfBirth"
                                                        value={formData.dateOfBirth}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="address" className="form-label fw-medium">
                                                Address
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light align-items-start pt-3 border-end-0">
                                                    <i className="fas fa-home text-muted"></i>
                                                </span>
                                                <textarea
                                                    className="form-control border-start-0"
                                                    id="address"
                                                    name="address"
                                                    rows="3"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    placeholder="Enter your full address"
                                                ></textarea>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-between pt-3 border-top">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary px-4"
                                                onClick={() => window.history.back()}
                                            >
                                                <i className="fas fa-arrow-left me-2"></i>
                                                Back
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary px-4"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-save me-2"></i>
                                                        Update Profile
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* Admin Access Tab */}
                        {activeTab === 'admin' && (
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '44px', height: '44px' }}>
                                            <i className="fas fa-user-shield text-primary"></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0 fw-bold text-dark">Admin Access</h5>
                                            <p className="text-muted mb-0 small">Request administrator privileges</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12">
                                            {/* Current Status */}
                                            <div className={`alert ${user.role === 'admin' ? 'alert-success' :
                                                user.role === 'pending' ? 'alert-warning' : 'alert-info'
                                                } mb-4`}>
                                                <div className="d-flex align-items-center">
                                                    <i className={`fas ${user.role === 'admin' ? 'fa-check-circle' :
                                                        user.role === 'pending' ? 'fa-clock' : 'fa-info-circle'
                                                        } me-2`}></i>
                                                    <div>
                                                        <strong>Current Status:</strong>
                                                        <span className="ms-2 text-capitalize fw-bold">
                                                            {user.role === 'admin' ? 'Administrator' :
                                                                user.role === 'pending' ? 'Pending Admin Approval' : 'Regular User'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Admin Request Section */}
                                            {user.role === 'user' && (
                                                <div className="card border-0 bg-light">
                                                    <div className="card-body p-4">
                                                        <div className="text-center mb-4">
                                                            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                                style={{ width: '80px', height: '80px' }}>
                                                                <i className="fas fa-user-shield fa-2x text-primary"></i>
                                                            </div>
                                                            <h4 className="fw-bold text-dark mb-3">Request Admin Access</h4>
                                                            <p className="text-muted mb-4">
                                                                Request administrator privileges to access the admin panel and manage the system.
                                                                Your request will be reviewed by existing administrators.
                                                            </p>
                                                        </div>

                                                        <div className="row mb-4">
                                                            <div className="col-md-4 mb-3">
                                                                <div className="text-center p-3 bg-white rounded-3 h-100">
                                                                    <i className="fas fa-cog fa-2x text-primary mb-3"></i>
                                                                    <h6 className="fw-bold">Admin Panel Access</h6>
                                                                    <p className="small text-muted mb-0">Access to comprehensive admin dashboard</p>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4 mb-3">
                                                                <div className="text-center p-3 bg-white rounded-3 h-100">
                                                                    <i className="fas fa-users fa-2x text-primary mb-3"></i>
                                                                    <h6 className="fw-bold">User Management</h6>
                                                                    <p className="small text-muted mb-0">Manage users and permissions</p>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4 mb-3">
                                                                <div className="text-center p-3 bg-white rounded-3 h-100">
                                                                    <i className="fas fa-chart-bar fa-2x text-primary mb-3"></i>
                                                                    <h6 className="fw-bold">Analytics & Reports</h6>
                                                                    <p className="small text-muted mb-0">View system analytics and reports</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="text-center">
                                                            <button
                                                                onClick={handleAdminRequest}
                                                                disabled={loading}
                                                                className="btn btn-primary btn-lg px-5"
                                                            >
                                                                {loading ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                                        Submitting Request...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fas fa-paper-plane me-2"></i>
                                                                        Submit Admin Request
                                                                    </>
                                                                )}
                                                            </button>
                                                            <p className="text-muted small mt-3">
                                                                After submission, please wait for approval from existing administrators.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Pending Approval Message */}
                                            {user.role === 'pending' && (
                                                <div className="card border-warning">
                                                    <div className="card-body text-center p-5">
                                                        <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                                            style={{ width: '100px', height: '100px' }}>
                                                            <i className="fas fa-clock fa-2x text-warning"></i>
                                                        </div>
                                                        <h4 className="fw-bold text-dark mb-3">Request Pending Approval</h4>
                                                        <p className="text-muted mb-4">
                                                            Your admin access request has been submitted and is currently under review.
                                                            You will be notified once an administrator approves your request.
                                                        </p>
                                                        <div className="alert alert-warning">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            Please be patient while we review your application.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Already Admin Message */}
                                            {user.role === 'admin' && (
                                                <div className="card border-success">
                                                    <div className="card-body text-center p-5">
                                                        <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                                            style={{ width: '100px', height: '100px' }}>
                                                            <i className="fas fa-check-circle fa-2x text-success"></i>
                                                        </div>
                                                        <h4 className="fw-bold text-dark mb-3">Admin Access Granted</h4>
                                                        <p className="text-muted mb-4">
                                                            You have full administrator privileges. You can access the admin panel and manage the system.
                                                        </p>
                                                        <a href="/admin" className="btn btn-success btn-lg px-5">
                                                            <i className="fas fa-tachometer-alt me-2"></i>
                                                            Go to Admin Panel
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '44px', height: '44px' }}>
                                            <i className="fas fa-lock text-primary"></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0 fw-bold text-dark">Security Settings</h5>
                                            <p className="text-muted mb-0 small">Manage your password and privacy</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handlePasswordChange}>
                                        <div className="mb-4">
                                            <h6 className="fw-medium mb-3">Change Password</h6>

                                            <div className="mb-3">
                                                <label htmlFor="currentPassword" className="form-label fw-medium">
                                                    Current Password
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-key text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="password"
                                                        className="form-control border-start-0"
                                                        id="currentPassword"
                                                        placeholder="Enter current password"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="newPassword" className="form-label fw-medium">
                                                    New Password
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-lock text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="password"
                                                        className="form-control border-start-0"
                                                        id="newPassword"
                                                        placeholder="Enter new password"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label htmlFor="confirmPassword" className="form-label fw-medium">
                                                    Confirm New Password
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fas fa-lock text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="password"
                                                        className="form-control border-start-0"
                                                        id="confirmPassword"
                                                        placeholder="Confirm new password"
                                                    />
                                                </div>
                                            </div>

                                            <div className="alert alert-info d-flex align-items-center">
                                                <i className="fas fa-info-circle me-2"></i>
                                                <div>
                                                    <strong>Coming Soon</strong> - Password change feature will be available in the next update.
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn btn-primary px-4"
                                                disabled
                                            >
                                                <i className="fas fa-key me-2"></i>
                                                Change Password
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;